// =============================================================================
// NAD Main Blog - Tag filtering + scroll-in animations
// =============================================================================

const ANIMATION_IO: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px 0px -8% 0px',
  threshold: 0.06,
};

export class NADMainBlog {
  private container: HTMLElement;
  private tags: NodeListOf<HTMLElement>;
  private cards: NodeListOf<HTMLElement>;
  private animationObserver: IntersectionObserver | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.tags = container.querySelectorAll<HTMLElement>('.nad-main-blog__tag');
    this.cards = container.querySelectorAll<HTMLElement>('.nad-main-blog__card');
    this.init();
    this.setupAnimations();
  }

  private init(): void {
    this.tags.forEach((tag) => {
      tag.addEventListener('click', () => this.filterByTag(tag));
    });
  }

  private setupAnimations(): void {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.revealAnimatedElementsImmediate();
      return;
    }

    this.animationObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || !this.animationObserver) {
          return;
        }
        const target = entry.target as HTMLElement;

        if (target.classList.contains('nad-main-blog__hero-inner')) {
          this.animateHero(target);
        } else if (target.classList.contains('nad-main-blog__filters')) {
          target.classList.add('animate-in');
        } else if (target.classList.contains('nad-main-blog__grid-section')) {
          this.animateGrid(target);
        }

        this.animationObserver.unobserve(target);
      });
    }, ANIMATION_IO);

    const heroInner = this.container.querySelector('.nad-main-blog__hero-inner');
    const filters = this.container.querySelector('.nad-main-blog__filters');
    const gridSection = this.container.querySelector('.nad-main-blog__grid-section');

    if (heroInner) {
      this.animationObserver.observe(heroInner);
    }
    if (filters) {
      this.animationObserver.observe(filters);
    }
    if (gridSection) {
      this.animationObserver.observe(gridSection);
    }
  }

  private animateHero(heroInner: HTMLElement): void {
    heroInner.querySelector('.nad-main-blog__hero-header')?.classList.add('animate-in');
    const featured = heroInner.querySelector('.nad-main-blog__featured');
    if (featured) {
      window.setTimeout(() => {
        featured.classList.add('animate-in');
      }, 130);
    }
  }

  private animateGrid(gridSection: HTMLElement): void {
    const cardList = gridSection.querySelectorAll<HTMLElement>('.nad-main-blog__card');
    const staggerMs = 72;

    cardList.forEach((card, index) => {
      window.setTimeout(() => {
        card.classList.add('animate-in');
      }, index * staggerMs);
    });

    const pagination = this.container.querySelector('.nad-main-blog__pagination');
    if (pagination) {
      const delay = Math.max(0, cardList.length) * staggerMs + 160;
      window.setTimeout(() => {
        pagination.classList.add('animate-in');
      }, delay);
    }
  }

  private revealAnimatedElementsImmediate(): void {
    const heroInner = this.container.querySelector('.nad-main-blog__hero-inner');
    heroInner?.querySelector('.nad-main-blog__hero-header')?.classList.add('animate-in');
    heroInner?.querySelector('.nad-main-blog__featured')?.classList.add('animate-in');
    this.container.querySelector('.nad-main-blog__filters')?.classList.add('animate-in');
    this.cards.forEach((card) => card.classList.add('animate-in'));
    this.container.querySelector('.nad-main-blog__pagination')?.classList.add('animate-in');
  }

  private filterByTag(activeTag: HTMLElement): void {
    const selectedTag = activeTag.dataset['tag'] ?? 'all';

    // Update active state
    this.tags.forEach((tag) => {
      const isActive = tag === activeTag;
      tag.classList.toggle('nad-main-blog__tag--active', isActive);
      tag.setAttribute('aria-selected', String(isActive));
    });

    // Filter cards
    this.cards.forEach((card) => {
      const cardTags = (card.dataset['tags'] ?? '').split(',').map((t) => t.trim());

      if (selectedTag === 'all' || cardTags.includes(selectedTag)) {
        card.classList.remove('is-hidden');
      } else {
        card.classList.add('is-hidden');
      }
    });
  }

  static initializeAll(): void {
    const containers = document.querySelectorAll<HTMLElement>('.nad-main-blog');
    containers.forEach((el) => {
      if (!(el as any)._nadMainBlogInstance) {
        (el as any)._nadMainBlogInstance = new NADMainBlog(el);
      }
    });
  }
}
